import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2, Sprout, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PreferenceCenter = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [topics, setTopics] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) { setError('This link is invalid.'); setLoading(false); return; }
    axios.get(`${API}/public/preferences/${token}`)
      .then((res) => {
        setData(res.data);
        const init = {};
        (res.data.topics || []).forEach((t) => { init[t.slug] = !!t.subscribed; });
        setTopics(init);
      })
      .catch(() => setError('This preference link is invalid or has expired.'))
      .finally(() => setLoading(false));
  }, [token]);

  const toggle = (slug) => setTopics((p) => ({ ...p, [slug]: !p[slug] }));

  const setAllSectors = (val) => {
    const sectors = (data?.topics || []).filter((t) => t.group === 'sector');
    setTopics((p) => {
      const next = { ...p };
      sectors.forEach((t) => { next[t.slug] = val; });
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/public/preferences/${token}`, { topics });
      setSaved(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const anyOn = Object.values(topics).some(Boolean);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl shadow-slate-200/60 border border-slate-100 p-8" data-testid="preference-center-page">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center mx-auto mb-6">
          <Sprout className="w-6 h-6 text-white" />
        </div>

        {loading ? (
          <Loader2 className="w-7 h-7 animate-spin text-slate-400 mx-auto" />
        ) : error ? (
          <div className="text-center">
            <h1 className="text-xl font-semibold text-slate-900 mb-2">Link not valid</h1>
            <p className="text-sm text-slate-500">{error}</p>
          </div>
        ) : saved ? (
          <div className="text-center" data-testid="preference-saved">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-slate-900 mb-2">
              {anyOn ? 'Preferences saved' : "You're unsubscribed"}
            </h1>
            <p className="text-sm text-slate-500">
              {anyOn
                ? 'Thanks - we\'ll only send you the emails you chose. You can change these anytime from any email we send.'
                : 'You won\'t receive any further emails from us. Sorry to see you go!'}
            </p>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-bold text-slate-900 mb-1 text-center">Your email preferences</h1>
            <p className="text-sm text-slate-500 mb-6 text-center">
              {data?.email && <span className="font-medium text-slate-700">{data.email}</span>}
              {' '}- choose what you'd like to hear about. Tick the boxes you want.
            </p>

            <div data-testid="preference-topics">
              <div className="space-y-3">
                {(data?.topics || []).filter((t) => t.group !== 'sector').map((t) => (
                  <label
                    key={t.slug}
                    className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${
                      topics[t.slug] ? 'border-blue-400 bg-blue-50/60' : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`pref-topic-${t.slug}`}
                  >
                    <input
                      type="checkbox"
                      checked={!!topics[t.slug]}
                      onChange={() => toggle(t.slug)}
                      className="mt-1 w-4 h-4 accent-blue-600"
                      data-testid={`pref-toggle-${t.slug}`}
                    />
                    <div>
                      <div className="text-sm font-semibold text-slate-900">{t.name}</div>
                      <div className="text-xs text-slate-500">{t.description}</div>
                    </div>
                  </label>
                ))}
              </div>

              {(data?.topics || []).some((t) => t.group === 'sector') && (
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-1">
                    <h2 className="text-sm font-semibold text-slate-900">Industry insights</h2>
                    <div className="flex gap-3 text-xs">
                      <button type="button" onClick={() => setAllSectors(true)} className="text-blue-600 hover:underline" data-testid="sectors-select-all">Select all</button>
                      <button type="button" onClick={() => setAllSectors(false)} className="text-slate-500 hover:underline" data-testid="sectors-clear-all">Clear</button>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mb-3">Pick the sectors you'd like news and insights for.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1" data-testid="preference-sectors">
                    {(data?.topics || []).filter((t) => t.group === 'sector').map((t) => (
                      <label
                        key={t.slug}
                        className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${
                          topics[t.slug] ? 'border-blue-400 bg-blue-50/60' : 'border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid={`pref-topic-${t.slug}`}
                      >
                        <input
                          type="checkbox"
                          checked={!!topics[t.slug]}
                          onChange={() => toggle(t.slug)}
                          className="w-4 h-4 accent-blue-600 shrink-0"
                          data-testid={`pref-toggle-${t.slug}`}
                        />
                        <span className="text-sm text-slate-800 leading-tight">{t.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={save}
              disabled={saving}
              className="w-full mt-6 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg px-5 py-3 text-sm inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
              data-testid="save-preferences-btn"
            >
              {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
                : anyOn ? <><Mail className="w-4 h-4" /> Save my preferences</> : 'Unsubscribe from everything'}
            </button>
            <p className="text-[11px] text-slate-400 text-center mt-4">
              Untick everything and save to stop all emails.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default PreferenceCenter;
